import { useState } from "react";
import { Icon } from "@mui/material";

import MDBox from "components/MDBox";
import MDButton from "components/MDButton";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";

import {
  getCountryList,
  getCountryStatusCount,
  activeCountry,
  deactiveCountry,
} from "services/data.internal.service";

import BasicTable from "ui-components/tables/BasicTable";
import { ListLayout, TableCard } from "ui-components/Layout";
import { EditButton, ActiveButton, DelButton } from "ui-components/IconButton";
import { Success, Error_, MessageDialog } from "ui-components/Alert";
import BasicTabs from "ui-components/BasicTabs";
import { useTeamValue } from "providers/TeamContext";
import { useCustomerValue } from "providers/CustomerContext";

function CountryList() {
  const navigate = useNavigate();
  const {permissions, allowAll} = useTeamValue();
  const {customerCode} = useCustomerValue()
  const [alert, setAlert] = useState(null);
  const [dialog, setDialog] = useState(null);
  const [status, setStatus] = useState("Active");
  const active = status === "Active" ? true : false;
  const [tabs, setTabs] = useState([
    { label: "Active", count: 0 },
    { label: "InActive", count: 0 },
  ]);
  const [data, setData] = useState([]);
  const queryClient = useQueryClient();
  const onError = (data) => setAlert(<Error_ data={data} onChange={setAlert} />);
  const onSuccess = (data) => {
    queryClient.invalidateQueries(["country-list"]);
    setAlert(<Success data={data} onChange={setAlert} />);
  };

  const addPermission = allowAll || (Boolean(permissions["Country"]) && permissions["Country"].findIndex(b => b==="new") !== -1);
  const editPermission = allowAll || (Boolean(permissions["Country"]) && permissions["Country"].findIndex(b => b==="edit") !== -1);
  const deactivatePermission = allowAll || (Boolean(permissions["Country"]) && permissions["Country"].findIndex(b => b==="deactivate") !== -1);

  useQuery({
    queryKey: ["country-list", { customerCode, active }],
    queryFn: () => getCountryList({ active }),
    onSuccess: (data) => setData(data.data.data),
    onError,
  });

  useQuery({
    queryKey: ["country-list", "status", { customerCode }],
    queryFn: () => getCountryStatusCount(),
    onSuccess: (data) => setTabs(data.data.data),
    onError,
  });

  const delMutation = useMutation(deactiveCountry, { onSuccess, onError });
  const activeMutation = useMutation(activeCountry, { onSuccess, onError });

  const onNew = () => navigate("/setup/country/add");

  const onEdit = (countryCode) => navigate("/setup/country/edit", { state: { countryCode } });

  const onDelete = (countryCode) => {
    setDialog(
      <MessageDialog
        title={`Deactivate ${countryCode}`}
        onClose={() => setDialog(null)}
        onOk={() => {
          delMutation.mutate({ countryCode });
          setDialog(null);
        }}
      />
    );
  };

  const onActive = (countryCode) => {
    setDialog(
      <MessageDialog
        title={`Activate ${countryCode}`}
        onClose={() => setDialog(null)}
        onOk={() => {
          activeMutation.mutate({ countryCode });
          setDialog(null);
        }}
      />
    );
  };

  const columns = [
    { header: "Country Code", accessorKey: "countryCode" },
    { header: "Country", accessorKey: "country" },
    { header: "Currency Code", accessorKey: "currencyCode" },
    {
      header: "Action",
      accessorKey: "action",
      enableColumnFilter: false,
      cell: ({ row }) => {
        const { countryCode } = row.original;
        if (active) {
          return (
            <>
              <EditButton title="edit country" disabled={!editPermission} onClick={() => onEdit(countryCode)} />
              <DelButton title="deactivate country" disabled={!deactivatePermission} onClick={() => onDelete(countryCode)} />
            </>
          );
        } else {
          return <ActiveButton title="activate country" disabled={!editPermission} onClick={() => onActive(countryCode)} />;
        }
      },
    },
  ];
  return (
    <ListLayout>
      {dialog}
      <MDBox my={3}>
        {alert}
        <BasicTabs tabs={tabs} status={status} setStatus={setStatus} />
      </MDBox>
      <TableCard>
        <MDBox display="flex" justifyContent="space-between" alignItems="flex-start" margin="10px">
          <MDButton variant="gradient" color="info" disabled={!addPermission} onClick={onNew}>
            <Icon>add</Icon>&nbsp;country
          </MDButton>
        </MDBox>
        <BasicTable columns={columns} data={data} />
      </TableCard>
    </ListLayout>
  );
}

export default CountryList;
